'use client'

import { useEffect, useCallback, useRef } from 'react'

import { useLocation } from '@marketplace-web/shared/browser'
import { UiState } from '@marketplace-web/shared/ui-state'
import { SCROLL_DELAY } from '@marketplace-web/domain/catalog-data'

type ScrollRestorationProps = {
  uiState: UiState
  itemsLength: number
}

const useItemsScrollRestoration = ({ uiState, itemsLength }: ScrollRestorationProps) => {
  const { urlQuery, relativeUrl } = useLocation()
  const initialUrlQueryRef = useRef(urlQuery)

  // capture initial urlQuery on mount
  useEffect(() => {
    initialUrlQueryRef.current = urlQuery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scrollKey = 'catalog_scroll_position'
  const queryKey = 'catalog_scroll_query'
  const pathKey = 'catalog_scroll_path'

  const clearStorage = useCallback(() => {
    sessionStorage.removeItem(scrollKey)
    sessionStorage.removeItem(queryKey)
    sessionStorage.removeItem(pathKey)
  }, [])

  const handleItemClick = useCallback(() => {
    sessionStorage.setItem(scrollKey, window.scrollY.toString())
    sessionStorage.setItem(queryKey, urlQuery)
    sessionStorage.setItem(pathKey, relativeUrl)
  }, [urlQuery, relativeUrl])

  // restore scroll position after API completion with delay
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined

    if (uiState === UiState.Success && itemsLength > 0) {
      timer = setTimeout(() => {
        const storedQuery = sessionStorage.getItem(queryKey)
        const storedPath = sessionStorage.getItem(pathKey)

        // check whether we are coming back from item page with exact same query
        const shouldSkip =
          storedQuery !== initialUrlQueryRef.current ||
          storedPath !== relativeUrl ||
          !storedQuery?.includes('time=') ||
          !initialUrlQueryRef.current.includes('time=')

        if (shouldSkip) {
          clearStorage()
        } else {
          const savedPosition = sessionStorage.getItem(scrollKey)
          if (savedPosition) {
            const scrollTop = parseFloat(savedPosition)
            window.scrollTo({ top: scrollTop })
          }
          clearStorage()
        }
      }, SCROLL_DELAY)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [uiState, itemsLength, relativeUrl, clearStorage])

  return { handleItemClick }
}

export default useItemsScrollRestoration
